import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PageTitle from '../../Component/Comman/PageTitle';
import { getCurrentUser, updateProfile, changePassword } from '../../Redux/Actions/authActions';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

function ProfilePage({ user, getCurrentUser, updateProfile, changePassword, loading }) {
    const hasFetched = useRef(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        gender: ''
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    useEffect(() => {
        // Only fetch once when component mounts
        if (!hasFetched.current && !loading && !user) {
            hasFetched.current = true;
            getCurrentUser();
        }
    }, [getCurrentUser, loading, user]);

    useEffect(() => {
        // Update form data when user data is loaded
        if (user) {
            setFormData({
                name: user.name || '',
                gender: user.gender || ''
            });
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        setSubmitError(null);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (passwordErrors[name]) {
            setPasswordErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        setPasswordError(null);
    };

    const validateProfileForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validatePasswordForm = () => {
        const errors = {};
        if (!passwordData.currentPassword.trim()) {
            errors.currentPassword = 'Current password is required';
        }
        if (!passwordData.newPassword.trim()) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters';
        }
        if (!passwordData.confirmPassword.trim()) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!validateProfileForm()) {
            return;
        }

        try {
            const result = await updateProfile({
                name: formData.name,
                gender: formData.gender || undefined
            });
            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    setSubmitSuccess(false);
                }, 3000);
            } else {
                setSubmitError(result.error || result.message || 'Failed to update profile');
            }
        } catch (error) {
            setSubmitError(error.message || 'Failed to update profile');
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError(null);
        setPasswordSuccess(false);

        if (!validatePasswordForm()) {
            return;
        }

        try {
            const result = await changePassword({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            if (result.success) {
                setPasswordSuccess(true);
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setTimeout(() => {
                    setShowPasswordModal(false);
                    setPasswordSuccess(false);
                }, 2000);
            } else {
                setPasswordError(result.error || result.message || 'Failed to change password');
            }
        } catch (error) {
            setPasswordError(error.message || 'Failed to change password');
        }
    };

    const handleClosePasswordModal = () => {
        setShowPasswordModal(false);
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordErrors({});
        setPasswordError(null);
        setPasswordSuccess(false);
    };

    if (loading && !user) {
        return (
            <>
                <div className="body-header border-bottom d-flex py-3 mb-3">
                    <PageTitle pagetitle='Profile' sidebutton={true} />
                </div>
                <div className="container-xxl">
                    <div className="card">
                        <div className="card-body">
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="body-header border-bottom d-flex py-3 mb-3">
                <PageTitle pagetitle='Profile' sidebutton={true} />
            </div>
            <div className="container-xxl">
                <div className="row g-3">
                    {/* Profile Information Card */}
                    <div className="col-lg-8">
                        <div className="card">
                            <div className="card-header py-3 bg-transparent border-bottom">
                                <h5 className="mb-0 fw-bold">Profile Information</h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleUpdateProfile}>
                                    {submitSuccess && (
                                        <div className="alert alert-success">
                                            <i className="icofont-check-circled me-2"></i>Profile updated successfully!
                                        </div>
                                    )}
                                    {submitError && (
                                        <div className="alert alert-danger">
                                            <i className="icofont-close-circled me-2"></i>{submitError}
                                        </div>
                                    )}
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {formErrors.name && (
                                                <div className="invalid-feedback">{formErrors.name}</div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Email</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={user?.email || ''}
                                                disabled
                                            />
                                            <small className="text-muted">Email cannot be changed</small>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Mobile Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={user?.mobileNumber || ''}
                                                disabled
                                            />
                                            <small className="text-muted">Mobile number cannot be changed</small>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Gender</label>
                                            <select
                                                className={`form-control ${formErrors.gender ? 'is-invalid' : ''}`}
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                            {formErrors.gender && (
                                                <div className="invalid-feedback">{formErrors.gender}</div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Status</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={user?.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'N/A'}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Role</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'N/A'}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label">Referral Code</label>
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={user?.referalCode || 'N/A'}
                                                    disabled
                                                />
                                                {user?.referalCode && (
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(user.referalCode);
                                                            alert('Referral code copied to clipboard!');
                                                        }}
                                                    >
                                                        <i className="icofont-copy"></i> Copy
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <div className="d-flex gap-2">
                                                <Button variant="primary" type="submit" disabled={loading}>
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        'Update Profile'
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Account Status Card */}
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header py-3 bg-transparent border-bottom">
                                <h5 className="mb-0 fw-bold">Account Status</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">Email Verification</span>
                                        <span className={`badge ${user?.isEmailVerified ? 'bg-success' : 'bg-warning'}`}>
                                            {user?.isEmailVerified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">Mobile Verification</span>
                                        <span className={`badge ${user?.isMobileVerified ? 'bg-success' : 'bg-warning'}`}>
                                            {user?.isMobileVerified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </div>
                                </div>
                                <div className="border-top pt-3">
                                    <Button
                                        variant="outline-primary"
                                        className="w-100"
                                        onClick={() => setShowPasswordModal(true)}
                                    >
                                        <i className="icofont-lock me-2"></i>Change Password
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <Modal show={showPasswordModal} onHide={handleClosePasswordModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleChangePassword}>
                    <Modal.Body>
                        {passwordSuccess && (
                            <div className="alert alert-success">
                                <i className="icofont-check-circled me-2"></i>Password changed successfully!
                            </div>
                        )}
                        {passwordError && (
                            <div className="alert alert-danger">
                                <i className="icofont-close-circled me-2"></i>{passwordError}
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label">Current Password <span className="text-danger">*</span></label>
                            <input
                                type="password"
                                className={`form-control ${passwordErrors.currentPassword ? 'is-invalid' : ''}`}
                                name="currentPassword"
                                value={passwordData.currentPassword}
                                onChange={handlePasswordChange}
                                required
                            />
                            {passwordErrors.currentPassword && (
                                <div className="invalid-feedback">{passwordErrors.currentPassword}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">New Password <span className="text-danger">*</span></label>
                            <input
                                type="password"
                                className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                required
                            />
                            {passwordErrors.newPassword && (
                                <div className="invalid-feedback">{passwordErrors.newPassword}</div>
                            )}
                            <small className="text-muted">Password must be at least 6 characters</small>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Confirm New Password <span className="text-danger">*</span></label>
                            <input
                                type="password"
                                className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                                name="confirmPassword"
                                value={passwordData.confirmPassword}
                                onChange={handlePasswordChange}
                                required
                            />
                            {passwordErrors.confirmPassword && (
                                <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClosePasswordModal} disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Changing...
                                </>
                            ) : (
                                'Change Password'
                            )}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>
        </>
    );
}

const mapStateToProps = (state) => ({
    user: state.auth?.user,
    loading: state.auth?.loading || false,
});

export default connect(mapStateToProps, {
    getCurrentUser,
    updateProfile,
    changePassword
})(ProfilePage);
